import { SupabaseClient, User } from '@supabase/supabase-js';
import { supabaseClient } from './supabaseClient';

class AuthService {
    private supabase: SupabaseClient;
    private currentUser: User | null = null;

    constructor() {
        this.supabase = supabaseClient;
        this.initializeAuth();
        this.setupVisibilityHandler();
    }

    private async initializeAuth() {
        try {
            const { data: { session } } = await this.supabase.auth.getSession();
            if (session) {
                this.currentUser = session.user;
            }

            this.supabase.auth.onAuthStateChange((event, session) => {
                this.currentUser = session?.user || null;
            });
        } catch (error) {
            console.error('Error initializing auth:', error);
        }
    }

    async signUp(email: string, password: string, displayName?: string) {
        const { data, error } = await this.supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    display_name: displayName,
                    email: email
                }
            }
        });
        if (error) throw error;
        return data;
    }

    async signIn(email: string, password: string) {
        const { data, error } = await this.supabase.auth.signInWithPassword({
            email,
            password
        });
        if (error) throw error;
        return data;
    }

    async signOut() {
        const { error } = await this.supabase.auth.signOut();
        if (error) throw error;
    }

    getCurrentUser(): User | null {
        return this.currentUser;
    }

    onAuthStateChange(callback: (event: string, session: any) => void) {
        return this.supabase.auth.onAuthStateChange(callback);
    }

    async getSession() {
        return await this.supabase.auth.getSession();
    }

    async refreshSession() {
        return await this.supabase.auth.refreshSession();
    }

    async getUser() {
        return await this.supabase.auth.getUser();
    }

    getCurrentUserId(): string | null {
        return this.currentUser?.id || null;
    }

    private setupVisibilityHandler() {
        let refreshTimeout: NodeJS.Timeout | null = null;

        // Handle page visibility changes to refresh session
        document.addEventListener('visibilitychange', async () => {
            if (!document.hidden && this.currentUser) {
                if (refreshTimeout) {
                    clearTimeout(refreshTimeout);
                }
                refreshTimeout = setTimeout(async () => {
                    console.log('[AuthService] Page became visible, refreshing session...');
                    try {
                        const { data, error } = await this.refreshSession();
                        if (!error && data.session) {
                            console.log('[AuthService] Session refreshed successfully');
                            this.currentUser = data.session.user;
                            
                            // Emit a custom event to notify other parts of the app
                            window.dispatchEvent(new CustomEvent('auth-session-refreshed', { 
                                detail: { user: data.session.user } 
                            }));
                        }
                    } catch (error) {
                        console.error('[AuthService] Failed to refresh session:', error);
                    }
                }, 1000); // Debounce for 1 second
            }
        });
    }
}

export const authService = new AuthService();